import React from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card'
import { Button } from '../../ui/button'
import { ExternalLink, CheckCircle2, XCircle } from 'lucide-react'

import RegistrationLayout from '../RegistrationLayout/RegistrationLayout'

function LTSInformation() {
  const history = useHistory()
  const { facility_id } = useParams()
  const dashboardUrl = `/facilities/${facility_id}/register`

  function handleContinue() {
    history.push(`${dashboardUrl}/students`)
  }

  return (
    <RegistrationLayout title="LTS Membership Required" headerColor="white" className="max-w-4xl px-4 mx-auto">
      <div className="py-8 space-y-6">
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-800">
              <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              You cannot register a student without a valid LTS number
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-red-900 leading-relaxed font-medium">
              Every student must have their own LTS USA or USFSA membership number before they can be registered for
              classes. This is a hard requirement — registration cannot be completed without it.
            </p>
            <ul className="space-y-2 text-sm text-red-800">
              <li>
                <strong>One number per student:</strong> Each student must have their own individual LTS number.
              </li>
              <li>
                <strong>Renewals:</strong> Parents check membership status and renew directly with LTS USA. Leevo
                collects the membership number but does not verify that the membership is active.
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card className="border-purple-200">
          <CardHeader>
            <CardTitle>Don&apos;t have an LTS number yet?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-800 leading-relaxed">
              You must register your student with Learn to Skate USA before you can complete this registration. Once you
              have your student&apos;s LTS number, return here to finish signing up for classes.
            </p>
            <a
              href="https://www.learntoskateusa.com/registration/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
            >
              Register at Learn to Skate USA
              <ExternalLink className="w-4 h-4" />
            </a>
            <p className="text-sm text-gray-500">
              Learn to Skate USA is the official learn-to-skate program of U.S. Figure Skating. Membership includes
              required liability insurance and skill level tracking.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Already have your LTS number?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-gray-700 leading-relaxed">
              You&apos;ll enter your student&apos;s LTS membership number in the next step. It&apos;s the 6–8 digit
              number from your LTS USA account or membership confirmation email.
            </p>
            <p className="text-sm text-gray-600">
              Can&apos;t find it? Look it up on the{' '}
              <a
                href="https://www.learntoskateusa.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-600 hover:text-purple-700 underline"
              >
                Learn to Skate USA website
              </a>{' '}
              or contact your skating program for assistance.
            </p>
            <div className="pt-2">
              <Button
                onClick={handleContinue}
                variant="outline"
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                data-cy="continue-button"
              >
                I have my LTS number — continue
                <CheckCircle2 className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </RegistrationLayout>
  )
}

export default LTSInformation
